import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useSendLoginToAuthMachine } from '@order/AuthMachine';
import type {
  ResendVerificationCodeInput,
  VerifyRegistrationInput,
} from '@order/graphql';
import {
  useResendVerificationCodeMutation,
  useVerifyRegistrationMutation,
} from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

export const useSignUpVerification = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const [signUpVerificationResponse, signUpVerificationMutation] =
    useVerifyRegistrationMutation();
  const signUpVerificationResponseData =
    signUpVerificationResponse.data?.verifyRegistration;
  const hasVerifiedCustomerRegistration =
    signUpVerificationResponseData?.__typename === 'VerifyRegistrationSuccess';

  const sendLoginAction = useSendLoginToAuthMachine();
  const [resendVerificationCodeResponse, resendVerificationCodeMutation] =
    useResendVerificationCodeMutation();
  const resendVerificationCodeResponseData =
    resendVerificationCodeResponse.data?.resendVerificationCode;
  const isResendingVerificationCode = resendVerificationCodeResponse?.fetching;
  const isVerificationCodeSent =
    resendVerificationCodeResponseData?.__typename ===
    'ResendVerificationCodeSuccess';

  // ────────────────────── Telemetry ───────────────────────────────────

  const { track } = useTelemetry();

  const onVerifySuccess = useCallback(() => {
    track('verify.success');
  }, [track]);

  const onVerifyFailed = useCallback(
    (userError?: string, systemError?: string) => {
      track('verify.failure', { userError, systemError });
    },
    [track],
  );

  const onResendVerificationSuccess = useCallback(() => {
    track('resend-verification.success');
  }, [track]);

  const onResendVerificationFailed = useCallback(
    (userError?: string, systemError?: string) => {
      track('resend-verification.failure', { userError, systemError });
    },
    [track],
  );

  // ────────────────────────────────────────────────────────────────────

  const verifySignUp = useCallback(
    async (input: VerifyRegistrationInput) => {
      const response = await signUpVerificationMutation({ input });
      const data = response?.data?.verifyRegistration;
      const isSuccessfullyVerified =
        data?.__typename === 'VerifyRegistrationSuccess';

      if (isSuccessfullyVerified) {
        onVerifySuccess();
        sendLoginAction(data?.csrf);

        return data;
      }

      const credentialsErrorCheck =
        data?.__typename === 'InvalidRegistrationCode';
      const systemError = getUrqlError(response?.error);
      const userError = credentialsErrorCheck
        ? t('join-verification.code.failure')
        : t('join-verification.generic.failure');

      onVerifyFailed(userError, systemError);
      addNoticeBanner({ text: userError, palette: 'caution' });

      return data;
    },
    [
      signUpVerificationMutation,
      t,
      onVerifyFailed,
      addNoticeBanner,
      onVerifySuccess,
      sendLoginAction,
    ],
  );

  const resendVerificationCode = useCallback(
    async (input: ResendVerificationCodeInput) => {
      const response = await resendVerificationCodeMutation({ input });
      const data = response?.data?.resendVerificationCode;
      const errorMessage = response.error?.message;

      const isVerificationCodeSuccessfullySent =
        data?.__typename === 'ResendVerificationCodeSuccess';

      if (isVerificationCodeSuccessfullySent) {
        onResendVerificationSuccess();
        addNoticeBanner({
          text: t('resend-verification-code.success', {
            email: input.email,
          }),
          palette: 'success',
        });

        return data;
      }

      if (errorMessage) {
        const userError = t('general.error');
        const systemError = getUrqlError(response.error);

        onResendVerificationFailed(userError, systemError);
        addNoticeBanner({ text: userError, palette: 'caution' });
      }

      return data;
    },
    [
      addNoticeBanner,
      onResendVerificationFailed,
      onResendVerificationSuccess,
      resendVerificationCodeMutation,
      t,
    ],
  );

  // ──────────────────────────────────────────────────────────────────

  return {
    isResendingVerificationCode,
    isVerificationCodeSent,
    resendVerificationCode,
    resendVerificationCodeResponseData,
    signUpVerificationResponseData,
    verifySignUp,
    hasVerifiedCustomerRegistration,
  };
};
